import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { MILESTONES } from "lib/constants";
import { useGetDashboardDetails } from "services/dashboard/hooks";
import { useMutateBinahSettings } from "services/face-scan/hooks";
import { BinahSetting } from "services/face-scan/type";
import { getFamilyMemberAttributes } from "services/profile/api";
import { useGetMemberDetails } from "services/profile/hooks";
import useAuthStore from "store/auth";
import useFaceScanStore from "store/face-scan";
import useFirstApprovedVisit from "./useFirstApprovedVisit";

export default function useGetFeatureStatus() {
  const { t } = useTranslation();

  const profileId = useAuthStore((store) => store.currentUserProfileId);
  const accountStatus = useAuthStore((store) => store.accountStatus);
  const adminDetails = useAuthStore((store) => store.adminDetails);
  const currentMilestone = useAuthStore((store) => store.currentMilestone);
  const isCurrentUserAdmin = useAuthStore((store) => store.isCurrentUserAdmin);
  const userAttributes = useAuthStore((store) => store.userAttributes);
  const memberDetails = useAuthStore((store) => store.memberDetails);
  const setUserAttributes = useAuthStore((store) => store.setUserAttributes);
  const setAdminDetails = useAuthStore((store) => store.setAdminDetails);
  const setMemberDetails = useAuthStore((store) => store.setMemberDetails);
  const setCurrentUserProfileId = useAuthStore(
    (store) => store.setCurrentUserProfileId
  );
  const setAdminProfileId = useAuthStore((store) => store.setAdminProfileId);
  const setSDKConfig = useFaceScanStore((store) => store.setSDKConfig);
  const setIncludeDemographic = useFaceScanStore(
    (store) => store.setIncludeDemographic
  );

  const { hasVisited, updateApprovedVisit } = useFirstApprovedVisit();

  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [isUpdatingDetails, setIsUpdatingDetails] = useState<boolean>(false);

  const { mutateAsync: fetchBinahSetting } = useMutateBinahSettings();

  const { data, isFetching } = useGetDashboardDetails({
    profileId,
    enabled:
      !!profileId &&
      Boolean(
        ![MILESTONES.TNC, MILESTONES.USER_DETAIL].includes(
          currentMilestone as MILESTONES
        )
      ),
  });
  const { data: detailsData, isFetching: isDetailsFetching } =
    useGetMemberDetails({
      enabled: !userAttributes && !memberDetails.length,
    });

  useEffect(() => {
    (async () => {
      if (profileId) {
        const data = await fetchBinahSetting();
        console.log("🚀 ~ useGetFeatureStatus ~ data:", data)
        if (data) {
          const demographic = data.find(
            (setting) => setting.setting_name === "demographic_flag"
          )?.setting_value;
          setIncludeDemographic(Boolean(demographic && demographic === "True"));
          storeBinahSettings(data);
        }
      }
    })();
  }, [profileId]);

  const updateUserAttribute = async (profile_id: string) => {
    try {
      const attributes = await getFamilyMemberAttributes({
        profile_id,
      });
      if (attributes) {
        setUserAttributes(attributes);
        if (!adminDetails) setAdminDetails(attributes);
      }
    } catch (error) {
      toast.error(t("failed_to_update_details_msg"));
    } finally {
      setIsUpdatingDetails(false);
    }
  };

  useEffect(() => {
    if (!userAttributes && detailsData?.length) {
      setIsUpdatingDetails(true);
      setMemberDetails(detailsData);

      const adminProfileId = detailsData.find(
        (member) => member.relation === "Admin"
      )!.profile_id;
      setCurrentUserProfileId(adminProfileId);
      setAdminProfileId(adminProfileId);

      updateUserAttribute(adminProfileId);
    }
  }, [detailsData, userAttributes]);

  const storeBinahSettings = async (data: BinahSetting[]) => {
    console.log("🚀 ~ storeBinahSettings ~ data:", data)
    try {
      const sdkKey = data?.find(
        (setting) => setting.setting_name === "sdk_key"
      )?.setting_value;

      const scanDuration = data?.find(
        (setting) => setting.setting_name === "scan_duration"
      )?.setting_value;

      setSDKConfig({
        binaah_sdk_key: sdkKey ?? "",
        scan_duration: scanDuration ? +scanDuration : 0,
      });
    } catch (error) {
      console.error(error);
      setSDKConfig({ binaah_sdk_key: "", scan_duration: 0 });
    }
  };

  useEffect(() => {
    if (showConfetti)
      toast(t("congratulations"), { icon: "🎉", duration: 8000 });
  }, [showConfetti]);

  const checkConfetti = useCallback(() => {
    if (
      accountStatus?.approved &&
      data &&
      !data?.scan?.flag &&
      !data?.questionnaire.flag &&
      !hasVisited
    ) {
      setShowConfetti(true);

      setTimeout(() => {
        setShowConfetti(false);
        updateApprovedVisit();
      }, 8000);
    } else {
      setShowConfetti(false);
    }
  }, [accountStatus, hasVisited, data]);

  return {
    isLoading: isFetching || isUpdatingDetails || isDetailsFetching,
    showConfetti: isCurrentUserAdmin && showConfetti,
    isQuestionnaireUpdated: data?.questionnaire.flag,
    isAIReportUpdated: data?.personalized_report.flag,
    isLabReportUpdated: data?.lab_report.flag,
    isScanUpdated: data?.scan.flag,
    checkConfetti,
    scanResults: data?.scan.flag ? data.scan.reading_id : [],
  };
}
